import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Connector cards — each opens an instructions modal instead of trying to
 * auto-install a connector to a URL we don't yet host (which was producing
 * the "no server active" errors). Once the relayer ships and the MCP URL
 * is live, swap `instructions` → real one-click `install` href.
 */

const MCP_URL = (import.meta.env.VITE_GAIA_MCP_REMOTE_URL ?? import.meta.env.VITE_GAIA_MCP_URL ?? "") as string;
const MCP_NAME = "gaia-mint";
const HAS_HOSTED_MCP = !!MCP_URL && !MCP_URL.includes("example");

type Connector = {
  id: string;
  label: string;
  sub: string;
  accent: string;
  icon: string;
  /** doc / install page */
  href: string;
  /** copy/paste recipe for setting up */
  instructions: { title: string; body: string; copy?: string }[];
};

// While the hosted MCP isn't live yet, every flow uses the LOCAL stdio MCP
// (`gaia-mcp-mint`). Once we ship `VITE_GAIA_MCP_URL=https://...`,
// the instructions automatically swap to the one-click hosted version.

const CONNECTORS: Connector[] = [
  {
    id: "claudecli",
    label: "Claude Code (CLI)",
    sub: HAS_HOSTED_MCP ? "claude mcp add (HTTP)" : "claude mcp add (local · stdio)",
    accent: "from-[#40b0c2] to-[#3ddc97]",
    icon: "$",
    href: "https://docs.anthropic.com/claude-code/mcp",
    instructions: HAS_HOSTED_MCP
      ? [
          { title: "Add the hosted MCP", body: `claude mcp add ${MCP_NAME} ${MCP_URL}`, copy: `claude mcp add ${MCP_NAME} ${MCP_URL}` },
          { title: "Verify", body: "claude mcp list", copy: "claude mcp list" },
          { title: "Use it", body: 'Inside a session: "mint me some GAIA"' },
        ]
      : [
          { title: "Add the local MCP", body: `claude mcp add ${MCP_NAME} -- gaia-mcp-mint`, copy: `claude mcp add ${MCP_NAME} -- gaia-mcp-mint` },
          { title: "Verify", body: "claude mcp list", copy: "claude mcp list" },
          { title: "Use it", body: 'Inside a session: "mint me some GAIA"' },
        ],
  },
  {
    id: "claude",
    label: "Claude.ai (web)",
    sub: HAS_HOSTED_MCP ? "custom connector · ready" : "needs hosted MCP · not live yet",
    accent: "from-[#cc7c5e] to-[#e8a87c]",
    icon: "A",
    href: "https://claude.ai/settings/connectors",
    instructions: HAS_HOSTED_MCP
      ? [
          { title: "Open connectors", body: "claude.ai → Settings → Connectors → Add custom connector" },
          { title: "Name", body: MCP_NAME, copy: MCP_NAME },
          { title: "Server URL", body: MCP_URL, copy: MCP_URL },
          { title: "Done", body: 'Say "mint me some GAIA" in chat.' },
        ]
      : [
          { title: "Heads up", body: "Hosted MCP isn't live yet — Claude web only accepts HTTP MCP servers." },
          { title: "Use the CLI instead", body: "Switch to Claude Code (CLI) for now — it can spawn the local stdio MCP via npx." },
          { title: "Coming soon", body: "We'll ship the hosted Streamable HTTP endpoint at https://gaia.earth/api/mcp." },
        ],
  },
  {
    id: "codex",
    label: "Codex / ChatGPT",
    sub: HAS_HOSTED_MCP ? "Apps → Developer Mode" : "Codex CLI · local stdio",
    accent: "from-[#10a37f] to-[#1fd1a5]",
    icon: "⌥",
    href: HAS_HOSTED_MCP ? "https://chatgpt.com/apps#settings/Connectors/Advanced" : "https://github.com/openai/codex",
    instructions: HAS_HOSTED_MCP
      ? [
          { title: "Open", body: "ChatGPT → Settings → Apps → Developer Mode ON" },
          { title: "Create app", body: `Name: ${MCP_NAME}\nServer URL: ${MCP_URL}\nAuth: none`, copy: MCP_URL },
          { title: "Prompt", body: 'In ChatGPT: "Use gaia-mint to mint GAIA"' },
        ]
      : [
          { title: "Edit ~/.codex/config.toml", body: `[mcp_servers.${MCP_NAME}]\ncommand = "npx"\nargs = ["-y", "@gaia/mcp-mint"]`, copy: `[mcp_servers.${MCP_NAME}]\ncommand = "npx"\nargs = ["-y", "@gaia/mcp-mint"]` },
          { title: "Prompt", body: 'In codex: "Use gaia-mint to mint GAIA"' },
        ],
  },
  {
    id: "cursor",
    label: "Cursor",
    sub: "settings.json · mcpServers",
    accent: "from-[#5b8bff] to-[#7ea8ff]",
    icon: "▶",
    href: "https://docs.cursor.com/context/mcp",
    instructions: HAS_HOSTED_MCP
      ? [
          { title: "Open Cursor settings", body: "Cmd/Ctrl + , → MCP" },
          { title: "Add server", body: `{\n  "mcpServers": {\n    "${MCP_NAME}": { "url": "${MCP_URL}" }\n  }\n}`, copy: `"${MCP_NAME}": { "url": "${MCP_URL}" }` },
        ]
      : [
          { title: "Open ~/.cursor/mcp.json", body: `{\n  "mcpServers": {\n    "${MCP_NAME}": {\n      "command": "npx",\n      "args": ["-y", "@gaia/mcp-mint"]\n    }\n  }\n}`, copy: `{\n  "mcpServers": {\n    "${MCP_NAME}": {\n      "command": "npx",\n      "args": ["-y", "@gaia/mcp-mint"]\n    }\n  }\n}` },
          { title: "Restart Cursor", body: "Cmd/Ctrl + Shift + P → Reload Window" },
        ],
  },
  {
    id: "cline",
    label: "Cline / Continue",
    sub: "VS Code · IDE",
    accent: "from-[#9b6bff] to-[#c4a5ff]",
    icon: "</>",
    href: "https://cline.bot/",
    instructions: [
      { title: "Open MCP servers", body: "Cline → settings → MCP servers" },
      { title: "Add stdio server", body: `Command: gaia-mcp-mint\nName: ${MCP_NAME}`, copy: `gaia-mcp-mint` },
    ],
  },
  {
    id: "dogeshit",
    label: "Install DogeShit Wallet",
    sub: "EIP-7702 · Chrome extension",
    accent: "from-[#f5c451] to-[#ff8a5c]",
    icon: "𝓓",
    href: "https://chromewebstore.google.com/detail/dogeshit-wallet/epbdphmhjbjiemaeabfkghhopangghli",
    instructions: [
      { title: "Install", body: "Open the Chrome Web Store link → Add to Chrome." },
      { title: "Create or import EOA", body: "Pin the extension, create a new account." },
      { title: "Fund with Sepolia ETH", body: "Use any Sepolia faucet for your new address." },
      { title: "Connect on this page", body: "Refresh, then click CONNECT DOGESHIT." },
    ],
  },
];

export function ConnectorButtons({ className }: { className?: string }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const active = CONNECTORS.find((c) => c.id === openId);

  return (
    <div className={className}>
      {/* Two-panel layout: cards on the left, details on the right */}
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-4">
        {/* LEFT — rectangular card grid */}
        <div className="grid grid-cols-2 gap-3">
          {CONNECTORS.map((c) => (
            <button
              key={c.id}
              onClick={() => setOpenId(c.id)}
              className={`ink-card group text-left p-4 cursor-pointer transition-all hover:border-teal-300/30 ${
                openId === c.id ? "ring-2 ring-teal-300/40 border-teal-300/40" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`size-11 rounded-xl bg-gradient-to-br ${c.accent} grid place-items-center font-mono text-sm text-ink-1000 shadow-[0_8px_24px_-8px_rgba(64,176,194,0.5)] font-bold shrink-0`}>
                  {c.icon}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-sm tracking-tight leading-tight">{c.label}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-dim mt-1 truncate">{c.sub}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* RIGHT — live instruction panel (no modal) */}
        <div className="ink-card p-5 relative min-h-[280px] flex flex-col">
          <AnimatePresence mode="wait">
            {active ? (
              <motion.div
                key={active.id}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col h-full"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`size-10 rounded-xl bg-gradient-to-br ${active.accent} grid place-items-center font-mono text-xs text-ink-1000 font-bold shrink-0`}>
                      {active.icon}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-base leading-tight truncate">{active.label}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-dim truncate">{active.sub}</div>
                    </div>
                  </div>
                  <a
                    href={active.href}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary text-xs py-1.5 px-3 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    open {active.label.split(" ")[0]} ↗
                  </a>
                </div>

                <div className="hairline mb-3" />

                <ol className="space-y-2 flex-1 overflow-y-auto pr-1">
                  {active.instructions.map((s, i) => (
                    <li key={i} className="rounded-xl bg-ink-900/60 p-3">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="size-6 rounded-full bg-teal-400 text-ink-1000 grid place-items-center font-mono text-xs font-bold">{i + 1}</span>
                        <span className="font-mono text-sm">{s.title}</span>
                      </div>
                      <pre className="font-mono text-[11px] text-bone whitespace-pre-wrap break-words bg-ink-1000/60 rounded-lg px-3 py-2">{s.body}</pre>
                      {s.copy && (
                        <button
                          onClick={() => navigator.clipboard?.writeText(s.copy ?? "")}
                          className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-teal-300 hover:text-teal-200"
                        >
                          copy ↗
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="h-full grid place-items-center text-bone-dim text-center px-6"
              >
                <div>
                  <div className="font-display text-xl mb-2">Select an agent</div>
                  <p className="font-mono text-xs max-w-[240px]">
                    Click a card on the left to see setup instructions and a direct link to the connector page.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-4 font-mono text-[11px] text-bone-dim text-center">
        click a card for setup instructions · web page cannot mint, only AI agents over MCP can
      </div>
    </div>
  );
}
